import { useTranslation } from 'react-i18next'

import {
  emptyCustomStorageConfig,
  type CustomStorageConfig,
  type CustomStorageProvider,
} from '@/features/studio/lib/custom-storage-config'
import { ProtoInput, ProtoLabel, ProtoSelect } from '@/features/studio/components/ui-kit'

type Props = {
  value: CustomStorageConfig
  onChange: (next: CustomStorageConfig) => void
  secretOptional?: boolean
  compact?: boolean
  /** 为 true 时不展示云厂商下拉（由外层 Tab 决定） */
  hideProvider?: boolean
}

export function CustomStorageConfigForm({
  value,
  onChange,
  secretOptional,
  compact,
  hideProvider,
}: Props) {
  const { t } = useTranslation()

  function setProvider(provider: CustomStorageProvider) {
    const defaults = emptyCustomStorageConfig(provider)
    onChange({
      ...defaults,
      bucket: value.bucket,
      access_key_id: value.access_key_id,
      access_key_secret: value.access_key_secret,
      public_base_url: value.public_base_url,
      prefix: value.prefix,
    })
  }

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'}>
      {!hideProvider ? (
        <div>
          <ProtoLabel required>{t('storageConfig.provider')}</ProtoLabel>
          <ProtoSelect
            value={value.provider}
            onChange={(e) => setProvider(e.target.value as CustomStorageProvider)}
          >
            <option value="aliyun_oss">{t('storageConfig.providerAliyun')}</option>
            <option value="tencent_cos">{t('storageConfig.providerTencent')}</option>
          </ProtoSelect>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <ProtoLabel required>{t('storageConfig.bucket')}</ProtoLabel>
          <ProtoInput
            value={value.bucket}
            onChange={(e) => onChange({ ...value, bucket: e.target.value })}
            placeholder={value.provider === 'tencent_cos' ? 'name-appid' : 'my-drama-assets'}
          />
        </div>
        <div>
          <ProtoLabel required>{t('storageConfig.region')}</ProtoLabel>
          <ProtoInput
            value={value.region}
            onChange={(e) => onChange({ ...value, region: e.target.value })}
            placeholder={value.provider === 'tencent_cos' ? 'ap-guangzhou' : 'oss-cn-hangzhou'}
          />
        </div>
        <div>
          <ProtoLabel required>{t('storageConfig.endpoint')}</ProtoLabel>
          <ProtoInput
            value={value.endpoint}
            onChange={(e) => onChange({ ...value, endpoint: e.target.value })}
            placeholder={
              value.provider === 'tencent_cos'
                ? 'https://cos.ap-guangzhou.myqcloud.com'
                : 'https://oss-cn-hangzhou.aliyuncs.com'
            }
          />
        </div>
        <div className="sm:col-span-2">
          <ProtoLabel required>
            {value.provider === 'tencent_cos' ? t('storageConfig.secretId') : t('storageConfig.accessKeyId')}
          </ProtoLabel>
          <ProtoInput
            value={value.access_key_id}
            onChange={(e) => onChange({ ...value, access_key_id: e.target.value })}
            autoComplete="off"
          />
        </div>
        <div className="sm:col-span-2">
          <ProtoLabel required={!secretOptional}>
            {value.provider === 'tencent_cos' ? t('storageConfig.secretKey') : t('storageConfig.accessKeySecret')}
          </ProtoLabel>
          <ProtoInput
            type="password"
            value={value.access_key_secret}
            onChange={(e) => onChange({ ...value, access_key_secret: e.target.value })}
            placeholder={secretOptional ? t('storageConfig.secretKeepPlaceholder') : undefined}
            autoComplete="new-password"
          />
        </div>
        <div className="sm:col-span-2">
          <ProtoLabel>{t('storageConfig.publicBaseUrl')}</ProtoLabel>
          <ProtoInput
            value={value.public_base_url}
            onChange={(e) => onChange({ ...value, public_base_url: e.target.value })}
            placeholder={t('storageConfig.publicBaseUrlPlaceholder')}
          />
          <p className="mt-1 text-[11px] text-zinc-500">{t('storageConfig.publicBaseUrlHint')}</p>
        </div>
        <div className="sm:col-span-2">
          <ProtoLabel>{t('storageConfig.prefix')}</ProtoLabel>
          <ProtoInput
            value={value.prefix}
            onChange={(e) => onChange({ ...value, prefix: e.target.value })}
            placeholder={t('storageConfig.prefixPlaceholder')}
          />
        </div>
      </div>
    </div>
  )
}
